import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { cn } from 'shared/utils'
import { Icon } from '@wmgtech/legato'

import { ConditionRender } from 'components'
import { AutoStoryArticleContent, StoryToggle } from 'modules/Feed/components'
import { AUTO_POST_ARTICLE_HEIGHT } from 'modules/Feed/consts'

import { AutoStoryArticleProps } from './AutoStoryArticle.props'

import './AutoStoryArticle.scss'

const AutoStoryArticle: FC<AutoStoryArticleProps> = ({
  storyProperties,
  isCollapsed,
  toggleCollapsed,
  toggleBigCard,
  article,
  title,
  type,
  isBigCard,
  setTouched,
  productLogo,
  dataTestPrefix
}) => {
  const articleRef = useRef<HTMLDivElement>(null)
  const toggleCollapsedHandler = useCallback(() => {
    setTouched(true)

    return isBigCard ? toggleCollapsed(!isCollapsed) : () => {}
  }, [isBigCard, isCollapsed, setTouched, toggleCollapsed])

  const expandHandler = useCallback(
    () => (isBigCard && isCollapsed ? toggleCollapsed(false) : () => {}),
    [isBigCard, isCollapsed, toggleCollapsed]
  )

  const storyPropertiesOptions = useMemo(() => {
    try {
      if (storyProperties) {
        return JSON.parse(storyProperties)
      }
      return null
    } catch {
      return null
    }
  }, [storyProperties])

  useEffect(() => {
    if (
      !isBigCard &&
      !isCollapsed &&
      articleRef.current?.clientHeight &&
      articleRef.current?.clientHeight > AUTO_POST_ARTICLE_HEIGHT
    ) {
      toggleCollapsed(true)
      toggleBigCard(true)
    }
  }, [articleRef, articleRef.current?.clientHeight, toggleBigCard, toggleCollapsed, isCollapsed, isBigCard])

  return (
    <div
      ref={articleRef}
      className={cn('story-article auto-story-article', {
        'story-article--collapsed': isCollapsed,
        'story-article--collapsed--social': isCollapsed
      })}
      onClick={expandHandler}
    >
      {isBigCard && (
        <StoryToggle
          isCollapsed={isCollapsed}
          toggleCollapsedHandler={toggleCollapsedHandler}
          dataTestPrefix={`${dataTestPrefix}`}
        />
      )}
      <Icon name={type === 'radio' ? 'tower-broadcast' : 'circle-bolt'} className="story-article__icon" />
      <div className="story-article-txt">
        <div className="story__body-info story-article__info">
          <img className="story-article-product__avatar" alt={title || ''} src={productLogo} />
          <h6 className="story-article-product__title">{title}</h6>
        </div>
        <ConditionRender
          condition={!!storyPropertiesOptions}
          component={<AutoStoryArticleContent type={type} storyProperties={storyPropertiesOptions} />}
          secondComponent={
            <div
              className="ql-editor ql-render"
              data-test={`${dataTestPrefix}-content`}
              dangerouslySetInnerHTML={{ __html: article }}
            />
          }
        />
      </div>
    </div>
  )
}

export default AutoStoryArticle
